// workspaceSkin.js — THE skin rule of the shell (HOME_AMBIENT_SKIN_SPEC §1,
// DESIGN-WL-SKINS-AMBIENT-DEFAULT-SOLID-WHITELABEL-1).
//
// Two skins on one token set, orthogonal to light/dark:
//   'ambient'  the DEFAULT (the Symbols look) — the sky ground, glass card /
//              pill / composer over it.
//   'solid'    the white-label look — a flat stage, the opaque card wash.
// The design system resolves the few tokens that differ at the ROOT
// (`:root[data-skin=…]`); no attribute is today's look (solid). Components
// never branch on the skin.
//
// The choice is the WORKSPACE's (Nika: "skin = workspace setting only"):
// `settings.appearance.skin`, written by the admin card on
// /admin/workspace-settings. Absent a choice the DEFAULT RULE answers: a
// white-label host, or a workspace that paints its own ground (`--stage-bg`
// or `--color-stage`), stays solid until its stage is measured; everyone else
// is ambient.
//
// ONE rule, two readers — the <html data-skin> stamp in app.js and the admin
// card — so the card can never show one skin while the shell paints another.
// Pure: the caller hands in what it holds (the workspaceSettings() answer and
// the host class), so this file imports nothing.
//
// THE SERVER RESOLVES IT TOO (core 9193ff2c, CORE-WORKSPACE-SETTINGS-
// APPEARANCE-SKIN-1): every workspace read carries `resolvedAppearance
// { skin, sky, skinSource, skySource }` beside `settings`, computed with the
// VERIFIED custom-domain signal the client can only guess from its hostname.
// The rule below reads it before its own default; the local default is the
// fallback for a record read before that field existed. The stage-override
// forms mirror the server's `hasStageOverride` so the two agree.

// Registered through functions/index.js (el.call('resolveWorkspaceSkin', …)
// works anywhere in the shell), so every export is SELF-CONTAINED: frank
// serializes each one alone (FA204) and a sibling read would revive as a
// ReferenceError. The two small checks are restated where they are used.
export const isWorkspaceSkin = (value) => value === 'ambient' || value === 'solid'

// resolveWorkspaceSkin(root, { info, brandedHost }) → { skin, chosen }
//
//   root         the shell root state (or an untracked view of it).
//   info         workspaceSettings(root) — { settings, ready, pending }.
//   brandedHost  the host is a white-label deployment (isCustomDomainHost()).
//
//   skin    'ambient' | 'solid' — or null while the workspace record is still
//           on its way (the answer would be a guess that flips on arrival;
//           the caller keeps what it has).
//   chosen  the workspace's own pick, or null when the default rule answered.
//
// Precedence: the member-visible copy `root.workspaceSettings` first — the
// admin card writes it optimistically, and applyWorkspaceConfig carries its
// keys forward — but only while its provenance stamp names the active
// workspace (the copy lags a switch); then the record's own pick (or the
// server's report of one); then THE MODULE'S GROUND: a workspace module that
// paints its own stage (`chrome.stage`, which the shell writes as
// `--stage-bg`) is solid until its stage is measured — the server cannot see
// a module's chrome, so this half is the client's; while a configured module
// (`settings.workspaceModule`) has not loaded, the answer is null (no guess
// that flips when it lands); then the server's resolved look (a list row
// carries it too, so a cold boot can stamp before the full record lands);
// then the local default rule.
export const resolveWorkspaceSkin = (root, opts) => {
  const r = root || {}
  const o = opts || {}
  const info = o.info || null
  const ws = r.workspace || null
  const wsId = ws ? String(ws.id || ws._id || '') || null : null
  const bag = r.workspaceSettings
  const bagHeld = !!bag && typeof bag === 'object'
  const bagCurrent = bagHeld && (!bag.workspace_id || String(bag.workspace_id) === wsId)
  const skinValue = (v) => v === 'ambient' || v === 'solid'
  const pick = (holder) =>
    holder && holder.appearance && skinValue(holder.appearance.skin) ? holder.appearance.skin : null
  // the same vars and forms as hasStageOverride below (restated — see the
  // note above)
  const stageOverride = (holder) => {
    const ds = holder && typeof holder === 'object' ? holder.designSystem : null
    if (!ds || typeof ds !== 'object') return false
    const filled = (v) => typeof v === 'string' && v.trim() !== ''
    return ['--stage-bg', '--color-stage'].some((name) => {
      const v = ds.vars && typeof ds.vars === 'object' ? ds.vars[name] : undefined
      if (filled(v)) return true
      if (v && typeof v === 'object' && (filled(v.light) || filled(v.dark))) return true
      return [ds.varsLight, ds.varsDark].some(
        (half) => !!half && typeof half === 'object' && filled(half[name])
      )
    })
  }

  const settings = (info && info.settings) || null
  const chosen = (bagCurrent && pick(bag)) || pick(settings)
  if (chosen) return { skin: chosen, chosen }

  const resolved = ws && ws.resolvedAppearance
  const resolvedSkin = resolved && skinValue(resolved.skin) ? resolved.skin : null
  if (resolvedSkin && resolved.skinSource === 'workspace') {
    return { skin: resolvedSkin, chosen: resolvedSkin }
  }

  // The module's ground (the same forms as ambientBackdrop.js
  // `ambientModuleStage`, restated — see the note above): a string, or a
  // { light, dark } pair, on the module's config chrome first.
  const wp = r.workspaceProject && typeof r.workspaceProject === 'object' ? r.workspaceProject : null
  if (wp) {
    const cfg = wp.config && typeof wp.config === 'object' ? wp.config : null
    const chrome = cfg && cfg.chrome !== undefined ? cfg.chrome : wp.chrome
    const stage = chrome && typeof chrome === 'object' ? chrome.stage : null
    const filled = (v) => typeof v === 'string' && v.trim() !== ''
    if (filled(stage) || (stage && typeof stage === 'object' && (filled(stage.light) || filled(stage.dark)))) {
      return { skin: 'solid', chosen: null }
    }
  } else if (settings && settings.workspaceModule) {
    return { skin: null, chosen: null }
  }

  // The record's own ground outranks the server's DEFAULT answer, for the same
  // reason the module's ground above does: the server answers 'default' only
  // when it saw no pick, no branded host and no ground, so a ground this
  // client reads is a form that server does not read yet (2026-10-30: a
  // server that knew only `--stage-bg` answered 'ambient' for a record that
  // paints `--color-stage`). Every other server answer still wins.
  const recordGround = (bagCurrent && stageOverride(bag)) || stageOverride(settings)
  if (resolvedSkin && !(recordGround && resolved.skinSource === 'default')) {
    return { skin: resolvedSkin, chosen: null }
  }
  if (info && info.pending) return { skin: null, chosen: null }

  if (o.brandedHost || r.whitelabelBrand) return { skin: 'solid', chosen: null }
  if (recordGround) return { skin: 'solid', chosen: null }
  return { skin: 'ambient', chosen: null }
}

// Does the workspace paint its own ground? Every ground reads ONE chain,
// `var(--stage-bg, var(--color-stage))` (shared/functions/canvasBackground.js),
// and workspaceConfigs.resolveDesignSystemVars writes every `vars` key on
// <html> — so the head (`--stage-bg`) and the tail (`--color-stage`) both
// repaint it. Either var, in any form that resolver accepts and the server
// checks (core workspaceAppearance.js hasStageOverride): a plain `vars` value,
// a `{ light, dark }` pair, or `varsLight` / `varsDark`.
// Measured 2026-10-30 on prod: the aihouse record sets only
// `vars['--color-stage']` (#3F4652); read as "no ground", it took the ambient
// glass (rgba(69,69,75,.45) dark), which sank into that slate — the left nav
// rail and the home cards showed no ground at all.
export const hasStageOverride = (holder) => {
  const ds = holder && typeof holder === 'object' ? holder.designSystem : null
  if (!ds || typeof ds !== 'object') return false
  const filled = (v) => typeof v === 'string' && v.trim() !== ''
  return ['--stage-bg', '--color-stage'].some((name) => {
    const v = ds.vars && typeof ds.vars === 'object' ? ds.vars[name] : undefined
    if (filled(v)) return true
    if (v && typeof v === 'object' && (filled(v.light) || filled(v.dark))) return true
    return [ds.varsLight, ds.varsDark].some(
      (half) => !!half && typeof half === 'object' && filled(half[name])
    )
  })
}

// The precondition for a skin write (the server's `expectedAppearance`
// compare-and-swap): the picks this client READ from the record, both
// contract fields, an absent pick as null. A write built on a stale read
// answers 409 instead of overwriting another admin's pick.
export const expectedAppearanceOf = (settings) => {
  const stored = settings && settings.appearance && typeof settings.appearance === 'object'
    ? settings.appearance
    : {}
  return {
    skin: stored.skin === undefined ? null : stored.skin,
    sky: stored.sky === undefined ? null : stored.sky
  }
}
